import React, { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useIsMobileViewport } from '../../hooks/useIsMobile';
import BrandLogo from '../BrandLogo';

/**
 * Dedicated admin portal chrome — visually and structurally separate from student AppShell.
 * Supports flat nav items and dropdown groups (e.g. Course Content subjects).
 */
export default function AdminShell({
  children,
  navItems = [],
  title = 'Administration',
  subtitle = '',
  headerExtra = null,
}) {
  const { logOut, currentUser } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const isMobile = useIsMobileViewport();
  const [menuOpen, setMenuOpen] = useState(false);
  const [openGroups, setOpenGroups] = useState(() => {
    const init = {};
    navItems.forEach((item) => {
      if (item.children) {
        init[item.key] = Boolean(
          item.defaultExpanded || item.children.some((c) => c.active)
        );
      }
    });
    return init;
  });

  useEffect(() => {
    setOpenGroups((prev) => {
      const next = { ...prev };
      navItems.forEach((item) => {
        if (item.children && (item.defaultExpanded || item.children.some((c) => c.active))) {
          next[item.key] = true;
        }
      });
      return next;
    });
  }, [navItems]);

  async function handleLogOut() {
    await logOut();
    navigate('/login');
  }

  function handleNav(path) {
    if (!path) return;
    navigate(path);
    setMenuOpen(false);
  }

  function toggleGroup(key) {
    setOpenGroups((prev) => ({ ...prev, [key]: !prev[key] }));
  }

  const sidebar = (
    <aside className={`sba-admin-sidebar${menuOpen ? ' is-open' : ''}`}>
      <div className="sba-admin-sidebar-brand">
        <BrandLogo size={42} />
        <div>
          <p className="sba-admin-brand-name">Success Bridge</p>
          <p className="sba-admin-brand-role">Admin Console</p>
        </div>
      </div>

      <div className="sba-admin-badge">Staff only</div>

      <nav className="sba-admin-nav">
        {navItems.map((item) => {
          if (item.children?.length) {
            const expanded = openGroups[item.key];
            const groupActive = item.children.some((c) => c.active);
            return (
              <div key={item.key} className="sba-admin-nav-group">
                <button
                  type="button"
                  className={`sba-admin-nav-item sba-admin-nav-parent${groupActive ? ' is-active' : ''}`}
                  onClick={() => toggleGroup(item.key)}
                  aria-expanded={expanded}
                >
                  <span className="material-symbols-outlined">{item.icon}</span>
                  <span className="sba-admin-nav-label">{item.label}</span>
                  <span
                    className={`material-symbols-outlined sba-admin-nav-caret${expanded ? ' is-open' : ''}`}
                  >
                    expand_more
                  </span>
                </button>
                {expanded ? (
                  <div className="sba-admin-nav-children">
                    {item.children.map((child) => (
                      <button
                        key={child.key || child.label}
                        type="button"
                        className={`sba-admin-nav-item sba-admin-nav-child${child.active ? ' is-active' : ''}`}
                        onClick={() => handleNav(child.path)}
                      >
                        <span
                          className="material-symbols-outlined"
                          style={child.color ? { color: child.active ? child.color : undefined } : undefined}
                        >
                          {child.icon}
                        </span>
                        {child.label}
                      </button>
                    ))}
                  </div>
                ) : null}
              </div>
            );
          }

          const isActive =
            item.active ??
            (item.path &&
              (location.pathname + location.search === item.path || location.pathname === item.path));

          return (
            <button
              key={item.key || item.label}
              type="button"
              className={`sba-admin-nav-item${isActive ? ' is-active' : ''}`}
              onClick={() => handleNav(item.path)}
            >
              <span className="material-symbols-outlined">{item.icon}</span>
              {item.label}
            </button>
          );
        })}
      </nav>

      <div className="sba-admin-sidebar-foot">
        <p className="sba-admin-user">{currentUser?.email || 'Admin'}</p>
        <button type="button" className="sba-admin-logout" onClick={handleLogOut}>
          <span className="material-symbols-outlined">logout</span>
          Sign out of admin
        </button>
      </div>
    </aside>
  );

  return (
    <div className="sba-admin-app">
      {isMobile && (
        <header className="sba-admin-mobile-bar">
          <button
            type="button"
            className="sba-admin-mobile-menu"
            aria-label="Menu"
            onClick={() => setMenuOpen((o) => !o)}
          >
            <span className="material-symbols-outlined">{menuOpen ? 'close' : 'menu'}</span>
          </button>
          <span className="sba-admin-mobile-title">Admin Console</span>
        </header>
      )}
      {isMobile && menuOpen ? (
        <button
          type="button"
          className="sba-admin-backdrop"
          aria-label="Close"
          onClick={() => setMenuOpen(false)}
        />
      ) : null}
      {sidebar}
      <main className="sba-admin-main">
        <header className="sba-admin-main-head">
          <div>
            <h1>{title}</h1>
            {subtitle ? <p>{subtitle}</p> : null}
          </div>
          {headerExtra ? <div className="sba-admin-head-extra">{headerExtra}</div> : null}
        </header>
        <div className="sba-admin-main-body">{children}</div>
      </main>
    </div>
  );
}
